import { jsx, jsxs } from "react/jsx-runtime";
import { Box, Stack, Columns, Column, Hidden, Heading, Text, Inline, Actions, ButtonLink } from "braid-design-system";
import * as React from "react";
import { createContext, useState, useContext, useCallback, useEffect } from "react";
import { URL_PRODUCTS_AD_BUDGETS } from "@seek/adv-constants";
import { useTranslations } from "@vocab/react";
import { createUrlResolver } from "@seek/melways-sites";
import { translations } from "../../HirerContractBalance/components/AdBudgetsUpsell/.vocab/index.mjs";
import { BalanceCarousel } from "./components/BalanceCarousel/BalanceCarousel.mjs";
import { ContractBalanceBreakdown } from "./components/Contract/ContractBalanceBreakdown.mjs";
import { HirerBalanceLayout } from "./components/HirerBalanceLayout.mjs";
import { root, widget } from "./HirerBalancePresentation.css.mjs";
const HirerBalanceContext = createContext(
  {}
);
const HirerBalanceContextProvider = ({
  children,
  site,
  language,
  isStaging,
  advertiserDetails,
  locale
}) => {
  const [calloutVisible, setCalloutVisible] = useState(false);
  const [contractBalance, setContractBalance] = useState();
  const [warningType, setWarningType] = useState("NoWarning");
  const showCallout = (contractBalanceToShow, warningTypeForContract) => {
    setCalloutVisible(true);
    setContractBalance(contractBalanceToShow);
    setWarningType(warningTypeForContract);
  };
  const hideCallout = () => {
    setCalloutVisible(false);
  };
  const urlResolver = createUrlResolver({
    site,
    language,
    staging: isStaging
  });
  const usersContext = {
    calloutVisible,
    contractBalance,
    warningType,
    advertiserDetails,
    showCallout,
    hideCallout,
    urlResolver,
    locale
  };
  return /* @__PURE__ */ jsx(HirerBalanceContext.Provider, { value: usersContext, children });
};
const compare = (first, second) => {
  if (first < second) {
    return -1;
  } else if (first > second) {
    return 1;
  }
  return 0;
};
const compareContracts = (first, second) => compare(first.expiryDate, second.expiryDate) || compare(first.validFromDate, second.validFromDate) || compare(first.createdDate, second.createdDate);
const centsToDollars = (cents) => Math.floor(cents / 100);
const mapConsumableContract = ({
  contractBalance,
  currentContract
}) => {
  var _a, _b;
  return {
    adType: contractBalance.adType,
    contractType: contractBalance.contractType,
    expiryDate: currentContract.expiry.expiryDate,
    validFromDate: currentContract.validFromDate,
    createdDate: currentContract.createdDate,
    isDollarBased: contractBalance.dollarBased,
    originalValue: contractBalance.dollarBased ? currentContract.value.dollars.original.value || 0 : ((_a = currentContract.value.units) == null ? void 0 : _a.original) || 0,
    remainingValue: contractBalance.dollarBased ? currentContract.value.dollars.remaining.value || 0 : ((_b = currentContract.value.units) == null ? void 0 : _b.remaining) || 0
  };
};
const mapAdType = (adType) => {
  switch (adType) {
    case "CLASSIC":
      return "classic";
    case "BRANDED":
      return "standout";
    case "BRANDED_UPGRADE":
      return "standout-upgrade";
    case "PREMIUM":
      return "premium";
    default:
      return "";
  }
};
const mapAdTypeForSeekContracts = (adType) => {
  switch (adType) {
    case "CLASSIC":
      return "classic";
    case "BRANDED":
      return "branded";
    default:
      return "";
  }
};
const createConsumableContractsTrackingData = (consumableContracts) => {
  const contractTypes = [];
  const isDollarBased = [];
  const originalValue = [];
  const remainingValue = [];
  const adTypes = [];
  consumableContracts.forEach((contract) => {
    contractTypes.push(
      contract.contractType === "SEEK_CONTRACT" ? "seek-contract" : "ad-pack"
    );
    isDollarBased.push(contract.isDollarBased);
    originalValue.push(centsToDollars(contract.originalValue));
    remainingValue.push(centsToDollars(contract.remainingValue));
    adTypes.push(
      contract.contractType === "SEEK_CONTRACT" ? mapAdTypeForSeekContracts(contract.adType) : mapAdType(contract.adType)
    );
  });
  return {
    hirerContractType: contractTypes,
    hirerContractIsDollarBased: isDollarBased,
    hirerContractValue: originalValue,
    hirerContractBalance: remainingValue,
    hirerContractAdType: adTypes
  };
};
const mapAdvertiserTypeHirer = (contractBalance) => {
  const isVariablePriceContract = (balance) => balance.contractType === "SEEK_CONTRACT";
  const isFixedPriceContract = (balance) => balance.contractType === "AD_PACK" || balance.contractType === "MASTER_AGREEMENT" || balance.contractType === "SUBSCRIPTION";
  if (contractBalance.some(isVariablePriceContract)) {
    return "contract";
  } else if (contractBalance.some(isFixedPriceContract)) {
    return "ad-pack";
  }
  return "casual";
};
const getConsumableContracts = (contractBalances) => {
  const potentialConsumableContracts = contractBalances.reduce((aggregator, balanceItem) => {
    var _a;
    const currentContract = (_a = balanceItem.contracts) == null ? void 0 : _a.find(
      (contract) => contract.current
    );
    return currentContract ? [
      mapConsumableContract({
        contractBalance: balanceItem,
        currentContract
      }),
      ...aggregator
    ] : aggregator;
  }, []);
  const sortedContracts = potentialConsumableContracts.sort(compareContracts);
  const consumableContractsMap = /* @__PURE__ */ new Map();
  const adTypesToBeGrouped = ["CLASSIC", "BRANDED"];
  const contractTypesToBeGrouped = ["ADPACK", "SEEK_CONTRACT"];
  sortedContracts.map((contract) => {
    const toBeGrouped = contractTypesToBeGrouped.includes(contract.contractType) && adTypesToBeGrouped.includes(contract.adType);
    const mapKey = toBeGrouped ? contract.contractType : `${contract.contractType}_${contract.adType}`;
    if (!consumableContractsMap.has(mapKey)) {
      consumableContractsMap.set(mapKey, contract);
    }
  });
  return Array.from(consumableContractsMap.values());
};
const advertiserHasStackedContracts = (contractBalances) => {
  const advertiserTotalContracts = contractBalances.reduce(
    (aggregator, balanceItem) => balanceItem.contracts ? aggregator + balanceItem.contracts.length : aggregator,
    0
  );
  return advertiserTotalContracts > 1;
};
const createImpressionTrackingData = (advertiserDetails) => {
  const { contractBalances, pricingModel, hideBalance } = advertiserDetails;
  const consumableContracts = getConsumableContracts(contractBalances);
  const advertiserTypeHirer = mapAdvertiserTypeHirer(contractBalances) || "casual";
  return {
    advertiserBalanceVisibility: hideBalance ? "hide-balance" : "show-balance",
    advertiserTypeHirer,
    advertiserTypePricing: pricingModel === "FIXED" ? "fixed" : "variable",
    advertiserHasStackedContracts: advertiserHasStackedContracts(contractBalances),
    ...consumableContracts.length > 0 && createConsumableContractsTrackingData(consumableContracts)
  };
};
const defaultTrackingCallback = () => {
};
const TrackingContext = React.createContext(
  defaultTrackingCallback
);
const TrackingProvider = ({ children, context }) => /* @__PURE__ */ jsx(TrackingContext.Provider, { value: context, children });
const onImpression = (trackingCallback) => (advertiserDetails) => {
  const impressionTrackingData = createImpressionTrackingData(advertiserDetails);
  return trackingCallback({
    eventName: ["balance_widget_displayed"],
    data: {
      linkName: "balance_widget_displayed",
      ...impressionTrackingData
    }
  });
};
const onBalanceWidgetClicked = (trackingCallback) => (input) => {
  const impressionTrackingData = createImpressionTrackingData(input);
  const contractTrackingData = input.currentConsumableContract && createConsumableContractsTrackingData([input.currentConsumableContract]);
  return trackingCallback({
    eventName: ["balance_widget_pressed"],
    data: {
      ...impressionTrackingData,
      linkName: "balance_widget_pressed",
      actionOrigin: input.actionOrigin,
      objectInteraction: input.objectInteraction,
      ...contractTrackingData
    }
  });
};
const useTracking = () => {
  const trackingContext = useContext(TrackingContext);
  const trackingCallback = trackingContext ?? defaultTrackingCallback;
  return {
    onImpression: useCallback(
      (advertiserDetails) => onImpression(trackingCallback)(advertiserDetails),
      [trackingCallback]
    ),
    onBalanceWidgetClicked: onBalanceWidgetClicked(trackingCallback)
  };
};
const PrepayAndSave = () => /* @__PURE__ */ jsx(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    width: "120",
    height: "120",
    viewBox: "0 0 120 120",
    children: /* @__PURE__ */ jsxs("g", { fill: "none", fillRule: "evenodd", children: [
      /* @__PURE__ */ jsx("path", { d: "M0 0H120V120H0z" }),
      /* @__PURE__ */ jsxs("g", { children: [
        /* @__PURE__ */ jsx(
          "path",
          {
            fill: "#FFF",
            d: "M59.524 17.238c-.9 2.576-3.718 3.935-6.295 3.035-2.576-.901-3.935-3.72-3.035-6.296.901-2.576 3.719-3.934 6.295-3.034 2.577.9 3.935 3.718 3.035 6.295zM65.222 4.83C62.857 1.217 57.566-.632 53.464.721L30 8.466c-4.102 1.354-8.624 5.797-10.048 9.875L.85 72.999c-1.424 4.077.746 8.579 4.823 10.003l45.69 15.966c4.077 1.426 8.578-.745 10.003-4.822l19.1-54.658c1.425-4.078.655-10.37-1.71-13.984L65.221 4.83z",
            transform: "translate(8 10)"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            fill: "#D8E3F2",
            fillRule: "nonzero",
            d: "M21.177 64.039c.17-.484.673-.756 1.162-.647l.112.033 27.98 9.777c.521.182.796.753.614 1.274-.169.484-.673.756-1.161.646l-.113-.032-27.98-9.777c-.521-.183-.796-.753-.614-1.274zM18.473 71.779c.169-.484.673-.756 1.161-.646l.113.032 27.98 9.778c.52.182.796.753.614 1.274-.17.484-.673.756-1.162.646l-.112-.032-27.98-9.778c-.522-.182-.797-.753-.614-1.274z",
            transform: "translate(8 10)"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            fill: "#0D3880",
            fillRule: "nonzero",
            d: "M38.899 38.244c1.248-3.571 5.155-5.455 8.725-4.206 3.57 1.248 5.454 5.154 4.206 8.725-.182.521-.752.796-1.273.614-.522-.182-.797-.752-.615-1.274.883-2.528-.45-5.293-2.978-6.177-2.527-.884-5.293.45-6.177 2.978-.517 1.48-.122 2.65 1.284 4.262l.283.317c.1.108.205.222.32.341l.944.964.496.502.531.497c3.416 3.24 4.364 5.05 3.326 8.018-1.247 3.571-5.153 5.454-8.724 4.207-3.571-1.249-5.455-5.155-4.206-8.725.182-.522.752-.797 1.274-.614.521.182.796.752.614 1.274-.884 2.527.45 5.293 2.978 6.177 2.528.883 5.293-.45 6.176-2.978.724-2.07-.038-3.345-3.36-6.42l-1.11-1.12-.394-.408c-.244-.258-.457-.49-.656-.717-1.831-2.1-2.46-3.96-1.664-6.237z",
            transform: "translate(8 10)"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            fill: "#0D3880",
            fillRule: "nonzero",
            d: "M47.575 31.148c.182-.522.752-.797 1.274-.615.484.17.756.673.646 1.162l-.032.112-1.224 3.505c-.183.52-.753.796-1.274.614-.484-.17-.756-.673-.647-1.162l.032-.112 1.225-3.504zM38.497 57.124c.182-.521.753-.796 1.274-.614.484.17.756.673.646 1.161l-.032.113-.982 2.813c-.182.521-.753.796-1.274.614-.484-.169-.756-.673-.646-1.161l.032-.113.982-2.813zM95.318 49.48c.513 0 .936.386.994.883l.006.117v5.498c0 .552-.447 1-1 1-.512 0-.935-.386-.993-.883l-.007-.117V50.48c0-.553.448-1 1-1zm-2.454-2.259c.552 0 1 .448 1 1 0 .513-.386.936-.884.994l-.116.006h-5.499c-.552 0-1-.447-1-1 0-.513.387-.935.884-.993l.116-.007h5.499zm10.211-.196c.553 0 1 .448 1 1 0 .513-.386.936-.883.993l-.117.007h-5.498c-.552 0-1-.447-1-1 0-.513.386-.935.883-.993l.117-.007h5.498zm-7.953-7.757c.513 0 .936.386.993.884l.007.116v5.499c0 .552-.448 1-1 1-.513 0-.935-.387-.993-.884l-.007-.116v-5.499c0-.552.448-1 1-1zm-6.687-22.904c.513 0 .935.386.993.883l.007.117v3.899c0 .552-.448 1-1 1-.513 0-.936-.386-.993-.884l-.007-.116v-3.9c0-.552.448-1 1-1zm-1.74-1.602c.551 0 1 .448 1 1 0 .513-.387.936-.884.993l-.117.007h-3.899c-.552 0-1-.448-1-1 0-.513.386-.935.883-.993l.117-.007h3.9zm7.241-.14c.552 0 1 .449 1 1 0 .514-.386.936-.883.994l-.117.007h-3.9c-.551 0-1-.448-1-1 0-.513.387-.936.884-.993l.117-.007h3.899zm-5.64-5.5c.512 0 .935.386.993.883l.007.117v3.899c0 .552-.448 1-1 1-.513 0-.936-.386-.994-.883l-.006-.117v-3.9c0-.551.447-1 1-1z",
            transform: "translate(8 10)"
          }
        )
      ] })
    ] })
  }
);
const AdBudgetsUpsell = () => {
  const { t } = useTranslations(translations);
  const { onBalanceWidgetClicked: onBalanceWidgetClicked2 } = useTracking();
  const { advertiserDetails, urlResolver } = useContext(HirerBalanceContext);
  const onViewDiscountsButtonClicked = () => {
    onBalanceWidgetClicked2({
      ...advertiserDetails,
      actionOrigin: "casual",
      objectInteraction: "view discounts"
    });
  };
  return /* @__PURE__ */ jsx(Box, { display: "flex", children: /* @__PURE__ */ jsx(Stack, { space: "gutter", children: /* @__PURE__ */ jsxs(Columns, { space: "gutter", collapseBelow: "desktop", children: [
    /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
      /* @__PURE__ */ jsx(Box, { paddingTop: { desktop: "medium" }, children: /* @__PURE__ */ jsx(PrepayAndSave, {}) }),
      /* @__PURE__ */ jsx(Hidden, { above: "tablet", children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
        /* @__PURE__ */ jsx(Heading, { level: "3", weight: "regular", children: t("Pay upfront and save!") }),
        /* @__PURE__ */ jsx(Text, { component: "p", children: t("Pay upfront and secure a discount on your ads.") })
      ] }) })
    ] }) }),
    /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
      /* @__PURE__ */ jsx(Hidden, { below: "desktop", children: /* @__PURE__ */ jsxs(Stack, { space: "gutter", children: [
        /* @__PURE__ */ jsx(Heading, { level: "3", weight: "regular", children: t("Pay upfront and save!") }),
        /* @__PURE__ */ jsx(Text, { component: "p", children: t("Pay upfront and secure a discount on your ads.") })
      ] }) }),
      /* @__PURE__ */ jsx(Inline, { space: "small", alignY: "center", collapseBelow: "desktop", children: /* @__PURE__ */ jsx(Actions, { children: /* @__PURE__ */ jsx(
        ButtonLink,
        {
          href: urlResolver({ path: URL_PRODUCTS_AD_BUDGETS }),
          onClick: onViewDiscountsButtonClicked,
          children: t("View discounts")
        }
      ) }) })
    ] }) })
  ] }) }) });
};
const HirerBalance = ({ contractBalances }) => {
  const hirerBalanceContext = useContext(HirerBalanceContext);
  const {
    calloutVisible,
    contractBalance: calloutContractBalance,
    warningType: calloutWarningType,
    advertiserDetails
  } = hirerBalanceContext;
  const noContracts = contractBalances.length === 0;
  if (noContracts || advertiserDetails.hideBalance) {
    return /* @__PURE__ */ jsx(HirerBalanceLayout, { upsell: true, children: /* @__PURE__ */ jsx(AdBudgetsUpsell, {}) });
  }
  return /* @__PURE__ */ jsx(Box, { className: root, children: /* @__PURE__ */ jsxs(Box, { position: "relative", background: "surface", className: widget, children: [
    calloutVisible && calloutContractBalance && /* @__PURE__ */ jsx(
      ContractBalanceBreakdown,
      {
        contractBalance: calloutContractBalance,
        warningType: calloutWarningType
      }
    ),
    /* @__PURE__ */ jsx(BalanceCarousel, { balances: contractBalances })
  ] }) });
};
const HirerBalancePresentation = (props) => {
  const { contractBalances, pricingModel, site, language, isStaging, locale } = props;
  const hideBalance = contractBalances.length > 0 ? contractBalances.every((balance) => balance.hideBalance) : false;
  const sortedContractBalances = contractBalances.filter((c) => c.dollarBased).concat(contractBalances.filter((c) => !c.dollarBased));
  const advertiserDetails = {
    contractBalances: sortedContractBalances,
    pricingModel,
    hideBalance
  };
  const { onImpression: onImpression2 } = useTracking();
  useEffect(() => {
    onImpression2({ contractBalances, hideBalance, pricingModel });
  }, [contractBalances, pricingModel, hideBalance, onImpression2]);
  return /* @__PURE__ */ jsx(
    HirerBalanceContextProvider,
    {
      advertiserDetails,
      site,
      language,
      isStaging,
      locale,
      children: /* @__PURE__ */ jsx(HirerBalance, { contractBalances: sortedContractBalances })
    }
  );
};
export {
  HirerBalanceContext,
  HirerBalancePresentation,
  PrepayAndSave,
  TrackingProvider,
  mapConsumableContract,
  useTracking
};
